import { ApiError, CreateIllegalRecordDto, IllegalRecord, Subject } from '@36node-fcp/core-sdk';
import { Alert, DatePicker, Form, Input, InputNumber, message, Modal } from 'antd';
import { DatePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { Uploader, VideoUploader } from 'src/components/uploader';
import { GantryDeviceSelect } from 'src/features/gantry-device';
import { IllegalRecordApiErrorMap } from 'src/features/illegal';
import { IllegalTypeSelect } from 'src/features/illegal-type';
import { useSession } from 'src/features/session';
import { useApi } from 'src/lib/react-api';
import { fcp, passFcpApiErrors } from 'src/services';

type EditorValues = CreateIllegalRecordDto & { expireDay?: number };

interface IllegalEditorProps {
  IllegalRecord?: IllegalRecord;
  onClose: () => void;
  onFinish?: () => void;
}

export const IllegalEditor: React.FC<IllegalEditorProps> = ({
  IllegalRecord,
  onClose: handleClose,
  onFinish: handleFinish,
}) => {
  const isEdit = !!IllegalRecord?.id;
  const onSuccess = () => {
    message.success(isEdit ? '编辑成功' : '创建成功');
    handleClose();
    handleFinish?.();
  };
  const onFailure = (err: ApiError) => {
    const ne = passFcpApiErrors(err, IllegalRecordApiErrorMap);
    if (ne.keys) {
      ne.keys.forEach((key) => form.setFields([{ name: key, errors: [ne.message[key]] }]));
    } else {
      message.error(ne.message as string);
    }
  };

  const [form] = Form.useForm<EditorValues>();
  const expireDay = Form.useWatch('expireDay', form);
  const capAt = Form.useWatch('capAt', form);
  const [updateState, update] = useApi(fcp.updateIllegalRecord, { onSuccess, onFailure });
  const [createState, create] = useApi(fcp.createIllegalRecord, { onSuccess, onFailure });
  const session = useSession();

  const handleOk = () => form.submit();
  const handleSubmit = async (values: EditorValues) => {
    if (values.expireDay) {
      values.expiredAt = dayjs(values.capAt).add(values.expireDay, 'day').toISOString();
    }
    const data = {
      subject: Subject.VEHICLE,
      source: '手动添加',
      capAt: values.capAt || dayjs().toISOString(),
      ...values,
    };
    delete data.expireDay;
    if (isEdit) {
      update({ illegalRecordId: IllegalRecord.id, body: data });
    } else {
      if (!data?.ns) {
        data.ns = session?.user?.ns;
      }
      create({ body: { ...data, ns: session?.user?.ns } });
    }
  };

  const isLoading = updateState.loading || createState.loading;
  const title = isEdit ? '编辑违法信息' : '录入违法信息';
  const error = updateState.error || createState.error;

  const disabledDate: DatePickerProps['disabledDate'] = (current) => {
    return current && current > dayjs();
  };

  return (
    <Modal title={title} open onOk={handleOk} onCancel={handleClose} confirmLoading={isLoading} width="650px">
      <Container>
        {error && <Alert message={error.message} type="error" />}
        <Form
          name="gantry-device-editor"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          initialValues={IllegalRecord}
          autoComplete="off"
          onFinish={() => handleSubmit(form.getFieldsValue(true))}
          form={form}
        >
          <div style={{ marginLeft: '-24px', marginBottom: '24px', fontSize: '16px' }}>基础信息</div>
          <Form.Item label="车牌" name="plate" rules={[{ required: true, message: '请填写车牌' }]}>
            <Input placeholder="填写车牌" />
          </Form.Item>
          <Form.Item label="号牌种类" name="plateType" rules={[{ required: true, message: '请填写号牌种类' }]}>
            <Input placeholder="填写号牌种类" />
          </Form.Item>
          <Form.Item label="违法名称" name="code" rules={[{ required: true, message: '请选择违法名称' }]}>
            <IllegalTypeSelect
              placeholder="选择违法名称"
              subject={[Subject.VEHICLE]}
              onChange={(value, option: any) =>
                form.setFieldsValue({
                  code: value,
                  level: option.item.level,
                  name: option.item.name,
                  expireDay: option.item.expireDay,
                })
              }
            />
          </Form.Item>
          <Form.Item label="车速" name="carSpeed">
            <InputNumber min={0} addonAfter={`km/h`} />
          </Form.Item>
          <Form.Item label="违法时间" name="capAt">
            <DatePicker
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="选择违法时间"
              showTime
              style={{ width: '100%' }}
              disabledDate={disabledDate}
            />
          </Form.Item>
          <Form.Item label="抓拍相机" name="gantryDevice">
            <GantryDeviceSelect
              placeholder="选择抓拍相机"
              onChange={(value, option: any) =>
                form.setFieldsValue({ gantryDevice: value, place: option.item.gantry.name, ns: option.item.gantry.ns })
              }
            />
          </Form.Item>
          <Form.Item label="违法地点" name="place">
            <Input placeholder="填写违法地点" />
          </Form.Item>
          <Form.Item label="所属部门" name="ns" hidden>
            <Input placeholder="填写违法地点" />
          </Form.Item>
          <Form.Item
            label="有效天数"
            name="expireDay"
            rules={[{ type: 'integer', min: 0, message: '有效天数无效' }]}
            extra={
              expireDay && (
                <span style={{ color: 'red' }}>{`${dayjs(capAt)
                  .add(expireDay, 'days')
                  .format('YYYY-MM-DD HH:mm:ss')} 后, 该违法信息失效`}</span>
              )
            }
          >
            <InputNumber placeholder="填写有效天数" style={{ width: '100%' }} />
          </Form.Item>
          <div style={{ marginLeft: '-24px', marginTop: '24px', marginBottom: '24px', fontSize: '16px' }}>图片信息</div>
          <Form.Item label="车辆照片" name="images">
            <Uploader setImages={(urls: string[]) => form.setFieldValue('images', urls)} />
          </Form.Item>
          <Form.Item label="上传视频" name="videos" extra="请上传视频文件，支持MP4格式">
            <VideoUploader setVideos={(urls: string[]) => form.setFieldValue('videos', urls)} />
          </Form.Item>
        </Form>
      </Container>
    </Modal>
  );
};

const Container = styled.div`
  margin: 24px;
`;
